import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export type AlertType = "alert" | "confirm" | "prompt";

export interface UnifiedAlertProps {
  open: boolean;
  type: AlertType;
  title?: string;
  message: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  onConfirm: (value?: string) => void;
  onCancel: () => void;
}

const UnifiedAlertOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
UnifiedAlertOverlay.displayName = "UnifiedAlertOverlay";

export function UnifiedAlert({
  open,
  type,
  title,
  message,
  defaultValue = "",
  confirmText,
  cancelText,
  variant = "default",
  onConfirm,
  onCancel,
}: UnifiedAlertProps) {
  const { t } = useTranslation("common");
  const [inputValue, setInputValue] = React.useState(defaultValue);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Reset input value when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setInputValue(defaultValue);
      // Auto-focus input for prompt type
      if (type === "prompt" && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [open, defaultValue, type]);

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (type === "prompt") {
        onConfirm(inputValue);
      } else {
        onConfirm();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const handleConfirm = () => {
    if (type === "prompt") {
      onConfirm(inputValue || "");
    } else {
      onConfirm();
    }
  };

  // Get button text from props or translations
  const getConfirmText = () => {
    if (confirmText) return confirmText;
    if (type === "alert") return t("alerts.ok", "OK");
    return t("alerts.confirm", "Confirm");
  };

  const getCancelText = () => {
    if (cancelText) return cancelText;
    return t("alerts.cancel", "Cancel");
  };

  // Get title - always provide one for accessibility
  const getTitle = () => {
    if (title) return title;
    if (type === "alert") return t("alerts.alert", "Alert");
    if (type === "confirm") return t("alerts.confirm", "Confirm");
    if (type === "prompt") return t("alerts.prompt", "Prompt");
    return t("alerts.alert", "Alert");
  };

  // Determine button variant
  const confirmButtonVariant = variant === "destructive" ? "destructive" : "default";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogPrimitive.Portal>
        <UnifiedAlertOverlay />
        <DialogPrimitive.Content
          className={cn(
            "card-container fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] p-6",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          )}
          onKeyDown={handleKeyDown}
        >
          <DialogPrimitive.Title className="text-lg font-semibold text-[#333333] mb-3">
            {getTitle()}
          </DialogPrimitive.Title>
          
          <DialogPrimitive.Description className="text-sm text-[#666666] mb-4">
            {message}
          </DialogPrimitive.Description>

          {type === "prompt" && (
            <div className="mb-4">
              <Input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleConfirm();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    onCancel();
                  }
                }}
                className="w-full"
                autoFocus
              />
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            {type !== "alert" && (
              <Button
                variant="outline"
                onClick={onCancel}
                className="h-8 px-4 text-xs font-medium"
              >
                {getCancelText()}
              </Button>
            )}
            <Button
              variant={confirmButtonVariant}
              onClick={handleConfirm}
              className="h-8 px-4 text-xs font-medium"
            >
              {getConfirmText()}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

